import {UserSearchAutocomplete} from '@hconnect/common/components/formFields'
import {ControlledFormField} from '@hconnect/common/types'
import {AutocompleteRenderGetTagProps, Chip} from '@mui/material'
import {useCallback, useState} from 'react'
import {Controller, FieldValues} from 'react-hook-form'

import {useFormInputLabelWithSuffix, useOilAnalysisUserSearch} from '../../hooks'

type User = {
  id: string
  name: string
}

type OilAnalysisUserSearchFormFieldProps<TField extends FieldValues> =
  ControlledFormField<TField> & {
    plantUpmId: string
    pleaseStartTypingLabel?: string
  }

export const OilAnalysisUserSearchFormField = <TField extends FieldValues>({
  plantUpmId,
  label,
  name,
  control,
  disabled,
  readOnly,
  optional,
  pleaseStartTypingLabel
}: OilAnalysisUserSearchFormFieldProps<TField>) => {
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined)

  const {data: userSuggestions, isFetching: isUsersLoading} = useOilAnalysisUserSearch(
    plantUpmId,
    searchTerm
  )

  const fieldDisabled = disabled || readOnly || !plantUpmId
  const inputLabel = useFormInputLabelWithSuffix({label, optional})
  const dataTestId = `oil-analysis-user-search-field-${name}`
  const handleUserSelectionChange = useCallback(
    (fieldOnChange: (...event: unknown[]) => void) =>
      (_event: unknown, newValue: User | User[] | null) => {
        if (!Array.isArray(newValue)) {
          fieldOnChange([])
          return
        }

        fieldOnChange(newValue)
      },
    []
  )
  const renderSelectedUsers = useCallback(
    (tagValue: User[], getTagProps: AutocompleteRenderGetTagProps) =>
      tagValue.map((option: User, index: number) => {
        const props = getTagProps({index})
        return (
          <Chip
            {...props}
            variant="outlined"
            size="small"
            key={option.id}
            label={option.name}
            onDelete={props.onDelete}
            data-test-id={`${dataTestId}-tag-${option.id}`}
          />
        )
      }),
    [dataTestId]
  )

  return (
    <Controller
      name={name}
      disabled={fieldDisabled}
      control={control}
      render={({field, fieldState}) => {
        const filterAvailableUsers = (suggestions, currentValues) =>
          ((suggestions as User[]) ?? []).filter(
            (user) => !(currentValues || []).find((assigned: User) => assigned.id === user.id)
          )

        return (
          <UserSearchAutocomplete
            multiple
            name={name}
            value={field.value ?? []}
            label={inputLabel}
            required={!optional}
            disabled={fieldDisabled}
            forcePopupIcon={false}
            helperText={fieldState.error?.message}
            error={!!fieldState.error?.message}
            disableCloseOnSelect={false}
            placeholder={pleaseStartTypingLabel}
            data-test-id={dataTestId}
            onChange={handleUserSelectionChange(field.onChange)}
            onInputChange={setSearchTerm}
            query={{
              data: filterAvailableUsers(userSuggestions, field.value),
              isFetching: isUsersLoading
            }}
            renderTags={renderSelectedUsers}
          />
        )
      }}
    />
  )
}
